import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle2,
    XCircle,
    Settings,
    Zap,
    Shield,
    CreditCard,
    Link2,
    Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import toast from 'react-hot-toast';
import {
    usePaymentMethod,
    usePaymentMethodProcessors,
    useAvailableProcessors,
    useUpdatePaymentMethodProcessors,
    type PaymentProcessorConfig,
    type AvailableProcessor,
} from '@/api/payment-methods/use-payment-methods';

export default function PaymentMethodProcessorConfig() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [processorConfigs, setProcessorConfigs] = useState<
        Record<string, { is_enabled: boolean; processor_settings: any }>
    >({});

    const { data: paymentMethod, isLoading: isLoadingMethod } = usePaymentMethod(id || '');
    const { data: currentProcessors, isLoading: isLoadingProcessors } = usePaymentMethodProcessors(
        id || ''
    );
    const { data: availableProcessors, isLoading: isLoadingAvailable } = useAvailableProcessors();
    const updateProcessors = useUpdatePaymentMethodProcessors();

    useEffect(() => {
        if (currentProcessors && availableProcessors) {
            const configs: Record<string, { is_enabled: boolean; processor_settings: any }> = {};

            // Initialize with current processor configs
            currentProcessors.forEach(proc => {
                configs[proc.tenant_integration_id] = {
                    is_enabled: proc.is_enabled,
                    processor_settings: proc.processor_settings || {},
                };
            });

            // Add available processors that aren't configured yet
            availableProcessors.forEach(proc => {
                if (!configs[proc.id]) {
                    configs[proc.id] = {
                        is_enabled: false,
                        processor_settings: {},
                    };
                }
            });

            setProcessorConfigs(configs);
        }
    }, [currentProcessors, availableProcessors]);

    const handleToggleProcessor = (integrationId: string, enabled: boolean) => {
        setProcessorConfigs(prev => ({
            ...prev,
            [integrationId]: {
                ...prev[integrationId],
                is_enabled: enabled,
            },
        }));
    };

    const handleSave = async () => {
        if (!id) return;

        try {
            const processor_configs = Object.entries(processorConfigs).map(
                ([tenant_integration_id, config]) => ({
                    tenant_integration_id,
                    is_enabled: config.is_enabled,
                    processor_settings: config.processor_settings,
                })
            );

            await updateProcessors.mutateAsync({
                paymentMethodId: id,
                data: { processor_configs },
            });

            toast.success('Payment method processors updated successfully');
        } catch (error: any) {
            console.error('Error updating processors:', error);
            toast.error(error?.response?.data?.message || 'Failed to update processors');
        }
    };

    const isLoading = isLoadingMethod || isLoadingProcessors || isLoadingAvailable;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!paymentMethod) {
        return (
            <div className="container mx-auto p-6">
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-muted-foreground">Payment method not found</p>
                        <Button onClick={() => navigate('/crm/payment-methods')} className="mt-4">
                            Back to Payment Methods
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const enabledCount = Object.values(processorConfigs).filter(c => c.is_enabled).length;

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/payment-methods')}
                        className="hover:bg-accent"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            Processor Configuration
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Configure payment processors for {paymentMethod.display_name}
                        </p>
                    </div>
                </div>
                <Button onClick={handleSave} disabled={updateProcessors.isPending}>
                    {updateProcessors.isPending ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>

            {/* Payment Method Info Card */}
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-2xl">{paymentMethod.display_name}</CardTitle>
                            <CardDescription className="mt-2">
                                {paymentMethod.description || 'No description'}
                            </CardDescription>
                        </div>
                        <Badge variant={paymentMethod.is_active ? 'default' : 'secondary'}>
                            {paymentMethod.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                            <p className="text-sm text-muted-foreground">Type</p>
                            <p className="font-medium">
                                {paymentMethod.is_fiat ? 'Fiat' : ''}
                                {paymentMethod.is_crypto ? 'Crypto' : ''}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Processing Time</p>
                            <p className="font-medium">
                                {paymentMethod.processing_time_description}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Min Amount</p>
                            <p className="font-medium">{paymentMethod.min_amount}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Max Amount</p>
                            <p className="font-medium">{paymentMethod.max_amount}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Processors List */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Link2 className="h-5 w-5" />
                                Available Processors
                            </CardTitle>
                            <CardDescription className="mt-2">
                                Enable or disable payment processors for this payment method.{' '}
                                {enabledCount} {enabledCount === 1 ? 'processor' : 'processors'}{' '}
                                currently enabled.
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {!availableProcessors || availableProcessors.length === 0 ? (
                        <div className="text-center py-12">
                            <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                                No payment processors available. Please configure integrations
                                first.
                            </p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => navigate('/admin/integrations')}
                            >
                                Go to Integrations
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {availableProcessors.map(processor => {
                                const config = processorConfigs[processor.id];
                                const isEnabled = config?.is_enabled || false;
                                const providerName = processor.provider_name.toUpperCase();

                                return (
                                    <Card
                                        key={processor.id}
                                        className={`transition-all duration-200 ${
                                            isEnabled
                                                ? 'border-primary/50 bg-primary/5 shadow-md'
                                                : 'border-border hover:border-primary/20'
                                        }`}
                                    >
                                        <CardContent className="pt-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div
                                                        className={`p-3 rounded-lg ${
                                                            isEnabled
                                                                ? 'bg-primary/10 text-primary'
                                                                : 'bg-muted text-muted-foreground'
                                                        }`}
                                                    >
                                                        {providerName === 'PGPAY' ? (
                                                            <Zap className="h-6 w-6" />
                                                        ) : providerName === 'TRANSAK' ? (
                                                            <Shield className="h-6 w-6" />
                                                        ) : providerName === 'PAYMESH' ? (
                                                            <CreditCard className="h-6 w-6" />
                                                        ) : (
                                                            <Settings className="h-6 w-6" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-lg">
                                                                {processor.provider_name}
                                                            </h3>
                                                            <Badge
                                                                variant={
                                                                    processor.is_active
                                                                        ? 'default'
                                                                        : 'secondary'
                                                                }
                                                                className="text-xs"
                                                            >
                                                                {processor.is_active
                                                                    ? 'Active'
                                                                    : 'Inactive'}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground mt-1">
                                                            Environment:{' '}
                                                            {processor.configuration.environment ||
                                                                'N/A'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <Label
                                                            htmlFor={`processor-${processor.id}`}
                                                            className="cursor-pointer"
                                                        >
                                                            {isEnabled ? (
                                                                <CheckCircle2 className="h-5 w-5 text-primary" />
                                                            ) : (
                                                                <XCircle className="h-5 w-5 text-muted-foreground" />
                                                            )}
                                                        </Label>
                                                        <Switch
                                                            id={`processor-${processor.id}`}
                                                            checked={isEnabled}
                                                            onCheckedChange={checked =>
                                                                handleToggleProcessor(
                                                                    processor.id,
                                                                    checked
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-muted/50">
                <CardContent className="pt-6">
                    <div className="flex gap-3">
                        <Settings className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div className="space-y-1">
                            <p className="text-sm font-medium">About Processor Configuration</p>
                            <p className="text-sm text-muted-foreground">
                                When a payment method has processors enabled, users will be able to
                                use those processors when making deposits or withdrawals. Only
                                active integrations can be enabled. Configure integrations in the
                                Integration Management page.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
